import { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api, errText, PHONE } from "@/lib/api";

export function Field({ label, error, children, id }) {
  return (
    <div>
      <label htmlFor={id} className="label">{label}</label>
      {children}
      {error && <p className="mt-1 text-xs text-red-400" data-testid={`${id}-error`}>{error}</p>}
    </div>
  );
}

export function FormResult({ status, message, reference, testPrefix }) {
  if (status === "success")
    return (
      <div className="flex gap-3 border border-emerald-500/40 bg-emerald-500/[0.06] p-5" data-testid={`${testPrefix}-success`} role="status">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" />
        <div><p className="font-semibold text-slate-100">Thanks — we've received it.</p>
          <p className="mt-1 text-sm text-slate-400">We'll reply as soon as we can, usually the same working day. Reference: <span className="font-mono text-slate-200">{reference}</span></p></div>
      </div>
    );
  if (status === "error")
    return (
      <div className="flex gap-3 border border-red-500/40 bg-red-500/[0.06] p-4" data-testid={`${testPrefix}-error`} role="alert">
        <AlertTriangle className="h-5 w-5 shrink-0 text-red-400" />
        <p className="text-sm text-slate-200">{message} If it keeps failing, call {PHONE}.</p>
      </div>
    );
  return null;
}

/** Shared submit state for public forms with client validation. */
export function useFormSubmit(endpoint, validate) {
  const loc = useLocation();
  const [state, setState] = useState({ status: "idle", errors: {}, message: "", reference: "" });
  const submit = async (values) => {
    const errors = validate(values);
    if (Object.keys(errors).length) return setState({ ...state, status: "invalid", errors });
    setState({ status: "busy", errors: {}, message: "", reference: "" });
    try {
      const r = await api.post(endpoint, { ...values, source_path: loc.pathname });
      setState({ status: "success", errors: {}, message: "", reference: r.data.reference });
    } catch (e) {
      setState({ status: "error", errors: {}, message: errText(e), reference: "" });
    }
  };
  return [state, submit];
}

export const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v || "");

export function SubmitButton({ busy, children, testId }) {
  return (
    <button type="submit" disabled={busy} className="btn-ice w-full sm:w-auto disabled:opacity-60" data-testid={testId}>
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Sending…" : children}
    </button>
  );
}

export function Honeypot({ value, onChange }) {
  return (
    <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
      <label>Website<input type="text" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} /></label>
    </div>
  );
}
